import { ChevronLeft, ChevronRight, ImageOff } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';

import type { AssetResponse } from '../../api';
import { FRAME_COPY_OPTIONS } from '../../constants/taxonomy';
import { CopyCaption } from './CopyCaption';
import { useFrameUrls } from './useFrameUrls';

/**
 * A carousel in the detail overlay, shown the way it runs: a deck you swipe.
 *
 * The current frame sits large in the middle with its neighbours peeking in
 * at either edge, smaller and dimmed, so the running order is visible at a
 * glance and a click on a neighbour slides the deck to it. Under the deck, the
 * progress segments and counter say where you are, and the caption card sets
 * that frame's copy as a layout would — headline first, disclaimer last — next
 * to the source image it was cut from.
 *
 * Frames move with the buttons, the segments and the peeking frames, never the
 * arrow keys: the overlay already gives those to stepping between assets, and a
 * carousel that swallowed them would trap the user inside it.
 *
 * Read-only, and the same on the Staged and Published tabs. URLs come from
 * `getFrameUrls` for this carousel alone, so the grid keeps signing one image
 * per card however many frames a carousel has.
 */

/** Space between frames on the deck, in px. */
const DECK_GAP = 28;
/** Share of the stage width the current frame may take, leaving room to peek. */
const FOCUS_SHARE = 0.6;

/**
 * Re-signings to try when a frame's image fails to load, the overlay's
 * `MAX_URL_RETRIES`: an expired signature recovers, a missing object does not.
 */
const MAX_URL_RETRIES = 1;

const pad = (value: number) => String(value).padStart(2, '0');

interface CarouselStageProps {
  asset: AssetResponse;
  /**
   * The frame on show, when the overlay keeps it in step with the frames
   * editor beside it. Left out, the stage keeps its own.
   */
  current?: number;
  onCurrentChange?: (position: number) => void;
  /**
   * Open this frame for editing. Given, the caption offers it — "Add copy"
   * where the frame has none, "Edit" where it has — so the way to the copy
   * starts where the copy is missing. Left out (read-only), it offers nothing.
   */
  onEditFrame?: (frameId: string) => void;
}

export function CarouselStage({
  asset,
  current: controlled,
  onCurrentChange,
  onEditFrame,
}: CarouselStageProps) {
  const frames = asset.frames ?? [];
  const [own, setOwn] = useState(0);
  const current = controlled ?? own;
  const setCurrent = (position: number) => {
    setOwn(position);
    onCurrentChange?.(position);
  };

  // No reset when the overlay moves to another asset: the caller keys this
  // component by asset id, so a new carousel starts fresh at frame 1, with its
  // deck measured anew.

  const { urlById, isLoading, isError, refetch } = useFrameUrls(asset.id);

  // A failed URL, not a failed frame: a fresh signature is a new URL and gets
  // its own try, while one that failed already shows as unavailable.
  const [failedUrls, setFailedUrls] = useState<ReadonlySet<string>>(new Set());
  const retries = useRef(0);
  const handleImageError = (url: string) => {
    setFailedUrls((current) => new Set(current).add(url));
    if (retries.current >= MAX_URL_RETRIES) return;
    retries.current += 1;
    void refetch();
  };

  // The deck is laid out in pixels from the stage's own size: every frame is
  // the carousel's pixel size, so one slot size fits them all, and centring
  // the current frame is a single translate of the track.
  const viewport = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const node = viewport.current;
    if (!node) return;
    const measure = () => setBox({ width: node.clientWidth, height: node.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const ratio = asset.width && asset.height ? asset.width / asset.height : 1;
  const slotHeight = Math.max(0, Math.min(box.height, (box.width * FOCUS_SHARE) / ratio));
  const slotWidth = slotHeight * ratio;
  const offset = -(current * (slotWidth + DECK_GAP) + slotWidth / 2);

  const frame = frames[current];
  if (!frame) {
    return (
      <div className="relative flex flex-col items-center gap-2 text-slate-400">
        <ImageOff className="h-8 w-8" strokeWidth={1.5} />
        <span className="text-[12.5px]">This carousel has no frames to show</span>
      </div>
    );
  }
  const go = (position: number) => setCurrent(Math.min(frames.length - 1, Math.max(0, position)));

  return (
    // Bottom padding keeps the caption clear of the overlay's "n of N" chip.
    <div className="relative flex h-full w-full flex-col items-center gap-4 pb-10">
      {/* The deck. The side fade lets the neighbours read as "more this way"
          rather than as cropped images. */}
      <div
        ref={viewport}
        className="relative min-h-0 w-full flex-1 overflow-hidden"
        style={{
          maskImage: 'linear-gradient(to right, transparent, black 14%, black 86%, transparent)',
          WebkitMaskImage:
            'linear-gradient(to right, transparent, black 14%, black 86%, transparent)',
        }}
      >
        <div
          className="absolute left-1/2 top-1/2 flex transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
          style={{ gap: DECK_GAP, transform: `translate(${offset}px, -50%)` }}
        >
          {frames.map((candidate, position) => {
            const signed = urlById.get(candidate.id);
            const url = signed && !failedUrls.has(signed) ? signed : undefined;
            const isCurrent = position === current;
            return (
              <button
                key={candidate.id}
                type="button"
                tabIndex={isCurrent ? -1 : 0}
                onClick={() => go(position)}
                aria-label={isCurrent ? undefined : `Show frame ${position + 1}`}
                style={{ width: slotWidth, height: slotHeight }}
                className={`relative shrink-0 overflow-hidden rounded-md bg-slate-100 transition-[transform,opacity,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${
                  isCurrent
                    ? 'cursor-default scale-100 opacity-100 shadow-[0_18px_48px_-12px_rgb(15_23_42/0.45)]'
                    : 'scale-[0.84] cursor-pointer opacity-40 hover:opacity-70'
                }`}
              >
                {url ? (
                  <img
                    src={url}
                    alt={
                      isCurrent
                        ? `Frame ${position + 1} of ${frames.length}: ${candidate.filename_original}`
                        : ''
                    }
                    draggable={false}
                    onError={() => handleImageError(url)}
                    className="h-full w-full object-cover"
                  />
                ) : isLoading && !signed ? (
                  <div className="h-full w-full animate-pulse bg-slate-200" />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-slate-400">
                    <ImageOff className="h-6 w-6" strokeWidth={1.5} />
                    {isCurrent && (
                      <span className="text-[11.5px]">
                        {isError ? 'Frames could not be loaded' : 'Preview unavailable'}
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Where you are: step, segments, count. */}
      <div className="flex w-full max-w-md items-center gap-3">
        <button
          type="button"
          disabled={current === 0}
          onClick={() => go(current - 1)}
          aria-label="Previous frame"
          className="rounded-full border border-slate-200 bg-white/90 p-1.5 text-slate-500 shadow-sm transition-colors hover:text-slate-900 disabled:opacity-30"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2} />
        </button>
        <div className="flex flex-1 gap-1" role="tablist" aria-label="Frames">
          {frames.map((candidate, position) => (
            <button
              key={candidate.id}
              type="button"
              role="tab"
              aria-selected={position === current}
              aria-label={`Frame ${position + 1}`}
              onClick={() => go(position)}
              className="group flex-1 py-2"
            >
              <span
                className={`block h-1 rounded-full transition-colors duration-300 ${
                  position === current
                    ? 'bg-[#0014cc]'
                    : position < current
                      ? 'bg-[#0014cc]/35 group-hover:bg-[#0014cc]/55'
                      : 'bg-slate-300 group-hover:bg-slate-400'
                }`}
              />
            </button>
          ))}
        </div>
        <span className="min-w-[3.25rem] text-right text-[12px] font-medium tabular-nums text-slate-500">
          <span className="text-slate-900">{pad(current + 1)}</span> / {pad(frames.length)}
        </span>
        <button
          type="button"
          disabled={current === frames.length - 1}
          onClick={() => go(current + 1)}
          aria-label="Next frame"
          className="rounded-full border border-slate-200 bg-white/90 p-1.5 text-slate-500 shadow-sm transition-colors hover:text-slate-900 disabled:opacity-30"
        >
          <ChevronRight className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>

      {/* The frame's caption: what it is cut from, and its copy as it reads. */}
      <CopyCaption
        key={frame.id}
        marker={pad(current + 1)}
        sourceCodes={frame.source_image_code ? [frame.source_image_code] : []}
        filename={frame.filename_original}
        copy={frame.copy_elements}
        subject="frame"
        typeOptions={FRAME_COPY_OPTIONS}
        onEdit={onEditFrame && (() => onEditFrame(frame.id))}
      />
    </div>
  );
}
